import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

interface AuthShellProps {
  title: string;
  /** Optional line under the heading, e.g. "Sign in to your account". */
  subtitle?: string;
  children: ReactNode;
}

/* ── Left-panel showcase ──────────────────────────────────────────────── */

function QuoteShowcase() {
  const { t } = useTranslation("onboarding");
  return (
    <blockquote className="mx-auto max-w-md px-8 text-center">
      <div className="mx-auto mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        <Sparkles className="h-5 w-5 text-primary" />
      </div>
      <p className="text-xl leading-relaxed xl:text-[1.35rem]">
        <span className="font-medium text-foreground">
          {t("shell.quote.lead")}
        </span>{" "}
        <span className="text-muted-foreground">{t("shell.quote.rest")}</span>
      </p>
    </blockquote>
  );
}

function ShowcasePanel() {
  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden bg-muted">
      {/* Brand bloom — a quiet wash of Graftus purple behind the quote. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(ellipse 55% 45% at 50% 42%, hsl(var(--primary) / 0.08), transparent 70%), radial-gradient(ellipse 40% 35% at 82% 88%, hsl(var(--primary) / 0.05), transparent 65%)",
        }}
      />
      {/* Dotted grid, faded toward the edges. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(var(--foreground) / 0.13) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage:
            "radial-gradient(ellipse 60% 55% at 50% 50%, black, transparent)",
        }}
      />
      <div className="relative z-10">
        <QuoteShowcase />
      </div>
    </div>
  );
}

/* ── Shell ────────────────────────────────────────────────────────────── */

/**
 * Auth chrome, matching the signup onboarding shell: a light showcase panel
 * on the left (editorial quote) and a sparse content column on the right with
 * a serif heading. Shared by sign-in and forgot-password.
 */
export default function AuthShell({
  title,
  subtitle,
  children,
}: AuthShellProps) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="grid min-h-dvh lg:grid-cols-2">
        {/* ── Showcase panel ── */}
        <aside className="relative hidden lg:block">
          <ShowcasePanel />
          <img
            src="/graftus-logo.svg"
            alt="Graftus"
            className="absolute left-8 top-7 z-20 h-6 w-auto"
          />
        </aside>

        {/* ── Content column ── */}
        <main className="relative flex min-h-dvh flex-col overflow-y-auto">
          {/* Mobile header */}
          <div className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur lg:hidden">
            <div className="flex items-center px-6 py-4">
              <img
                src="/graftus-logo.svg"
                alt="Graftus"
                className="h-5 w-auto dark:brightness-0 dark:invert"
              />
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12 xl:px-20">
            <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h1 className="font-display text-[1.7rem] leading-tight tracking-tight text-foreground sm:text-3xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                  {subtitle}
                </p>
              )}
              <div className="mt-8">{children}</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
